import { useCallback, useContext, useEffect, useState } from 'react';

import type { Provider } from '@atlaskit/collab-provider/provider';
import type { ProviderError, CollabEditProvider } from '@atlaskit/editor-common/collab';
import { PROVIDER_ERROR_CODE } from '@atlaskit/editor-common/collab';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import {
	EDIT_PAGE_EXPERIENCE,
	EDIT_PAGE_LOAD_EXPERIENCE,
	VIEW_PAGE_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import { isUnrecoverableError } from './collabErrors.ts';

type NativeCollabError = {
	error: ProviderError | null;
};

const isNotPermittedError = (error: ProviderError) => {
	return (
		(error.code === PROVIDER_ERROR_CODE.NO_PERMISSION_ERROR && 'EXPIRED_TOKEN' !== error.reason) ||
		error.code === PROVIDER_ERROR_CODE.DOCUMENT_NOT_FOUND
	);
};

const isTenantSuspendedError = (error: ProviderError) => error.status === 423;

/**
 * The document has been blocked (blacklisted) by NCS, e.g. via the documentari kill switch.
 * https://switcheroo.atlassian.com/ui/gates/1a5f36bd-0eb6-4634-ba32-ae325174edb8/key/documentari_kill_switch
 * This is an expected, non-recoverable precondition (not a failure), so we disconnect and abort rather than
 * fire an error/reconnect loop. The DOCUMENT_BLOCKED code is only ever emitted when
 * platform_editor_blocked_document_ux is on (gated in the collab-provider errorCodeMapper), so the code
 * alone is the reliable signal here.
 */
const isDocumentBlockedError = (error: ProviderError) =>
	error.code === PROVIDER_ERROR_CODE.DOCUMENT_BLOCKED;

/**
 * Listens for collab service error & provides error to caller. Some errors may have additional logic before being returned.
 * Error is emitted after the collab service is initialised.
 */
export const useError = (
	collabEditProvider: (CollabEditProvider & Provider) | null,
	recover: () => Promise<void>,
	contentId: string,
	contentType: string,
): NativeCollabError => {
	const [error, setError] = useState<ProviderError | null>(null);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isLivePage = useIsCurrentPageLive();
	const currentEditorPageLoadExperienceName = isLivePage
		? VIEW_PAGE_EXPERIENCE
		: EDIT_PAGE_LOAD_EXPERIENCE;

	const setErrorAndDisconnect = useCallback(
		(newError: ProviderError) => {
			if (isDocumentBlockedError(newError)) {
				// Document is blocked (blacklisted). Surface the error so the UI can show a blocked
				// message, stop the provider from reconnecting, and abort (not fail) the load
				// experience so the editor settles into a disabled state instead of spinning.
				setError(newError);
				collabEditProvider?.disconnect();
				const blockedExperienceState = experienceTracker.getExperienceState(
					currentEditorPageLoadExperienceName,
				);
				if (blockedExperienceState && !blockedExperienceState.hasStopped) {
					experienceTracker.abort({
						name: currentEditorPageLoadExperienceName,
						reason: 'document blocked',
						attributes: {
							errorStatus: newError.status,
							errorName: newError.code,
							errorMessage: newError.message,
							objectId: contentId,
							objectType: contentType,
							isLivePage,
						},
					});
				}
				return;
			}

			if (
				isNotPermittedError(newError) ||
				isUnrecoverableError(newError) ||
				isTenantSuspendedError(newError)
			) {
				setError(newError);
			}

			const experienceState = experienceTracker.getExperienceState(
				currentEditorPageLoadExperienceName,
			);
			if (!experienceState || experienceState.hasStopped) {
				// initialisation completed but we received an error later
				if (!isNotPermittedError(newError)) {
					if (isUnrecoverableError(newError)) {
						experienceTracker.stopOnError({
							name: EDIT_PAGE_EXPERIENCE,
							error: {
								name: newError.code,
								message: newError.message,
							},
							attributes: {
								errorStatus: newError.status,
								objectId: contentId,
								objectType: contentType,
								isLivePage,
							},
						});
					} else {
						createAnalyticsEvent({
							type: 'sendOperationalEvent',
							data: {
								action: 'error',
								actionSubject: 'nativeEditor',
								source: 'confluenceFrontend',
								objectId: contentId,
								objectType: contentType,
								attributes: {
									errorName: newError.code,
									errorMessage: newError.message,
									errorStatus: newError.status,
									isRecoverable: true,
									reason: 'reason' in newError ? newError.reason : 'unknown',
									isLivePage,
								},
							},
						}).fire();
					}
				}
				return;
			}

			if (isNotPermittedError(newError)) {
				// expected 403 for restricted page or deleted page
				// for deleted page, 403 is returned with reason "RESOURCE_DELETED"
				let newErrorReason: string | undefined;
				if ('reason' in newError) {
					newErrorReason = newError.reason;
				}
				collabEditProvider!.disconnect();
				experienceTracker.abort({
					name: currentEditorPageLoadExperienceName,
					reason: newErrorReason ?? 'page restricted or deleted',
					attributes: {
						errorStatus: newError.status,
						errorName: newError.code,
						errorMessage: newError.message,
						objectId: contentId,
						objectType: contentType,
						isLivePage,
					},
				});
			} else if (isUnrecoverableError(newError)) {
				// unhandled/unexpected errors
				experienceTracker.stopOnError({
					name: currentEditorPageLoadExperienceName,
					error: {
						name: newError.code,
						message: newError.message,
					},
					attributes: {
						errorStatus: newError.status,
						objectId: contentId,
						objectType: contentType,
						isLivePage,
					},
				});
			}
		},
		[
			collabEditProvider,
			experienceTracker,
			createAnalyticsEvent,
			contentId,
			contentType,
			currentEditorPageLoadExperienceName,
			isLivePage,
		],
	);

	useEffect(() => {
		if (!collabEditProvider) {
			return;
		}

		const handleError = (emittedError: ProviderError) => {
			if (emittedError.status == 404) {
				// 404 means that collab service doesn't have the document but Confluence does
				// Reset the document before displaying an error (creates it in NCS if missing).
				void recover();
			} else {
				setErrorAndDisconnect(emittedError);
			}
		};
		collabEditProvider.on('error', handleError);

		return () => {
			collabEditProvider.off('error', handleError);
		};
	}, [collabEditProvider, recover, setErrorAndDisconnect]);

	return {
		error,
	};
};
